import type { FlowDetail } from "@/features/flows/model";
import { CreativeMedia } from "./CreativeBits";
import { safeMediaHref } from "@/lib/safe-url";
import { Link } from "react-router";
import { useTool } from "@/api/hooks";
import { ButtonLink, ErrorState, Pill, Skeleton, type Tone } from "@/components/ui";
import { brandPath } from "@/state/brand";

/** Durable generation receipts returned by flows.list/get and creatives.list. */
export interface Generation {
  flowId: string;
  runId: string;
  title: string;
  sourceTool: string;
  sourceId: string | null;
  status: "queued" | "running" | "blocked" | "failed" | "cancelled" | "completed";
  progress: number;
  stage: string;
  jobIds: string[];
  batchIds: string[];
  creativeIds: string[];
  assetIds: string[];
  error: string | null;
  url: string;
}

export function generationsOf(value: unknown): Generation[] {
  if (!value || typeof value !== "object") return [];
  const rows = (value as { generations?: unknown }).generations;
  return Array.isArray(rows) ? rows.filter((g): g is Generation => !!g && typeof g === "object" && typeof g.flowId === "string" && typeof g.runId === "string") : [];
}

export const generationActive = (g: Generation) => ["queued", "running", "blocked"].includes(g.status);
export const generationHref = (brandId: string, g: Pick<Generation, "flowId" | "runId">) => `${brandPath(brandId, `flows/${encodeURIComponent(g.flowId)}`)}?run=${encodeURIComponent(g.runId)}`;
export function generationReceipt(value: unknown): Pick<Generation, "flowId" | "runId"> | null {
  if (!value || typeof value !== "object") return null;
  const r = value as Record<string, unknown>;
  return typeof r.flowId === "string" && typeof r.runId === "string" ? { flowId: r.flowId, runId: r.runId } : null;
}

/** No timer or optimistic completion: every arc comes from persisted completed stages. */
export function GenerationRing({ generation: g, small = false }: { generation: Generation; small?: boolean }) {
  const raw = Number.isFinite(g.progress) ? g.progress : 0;
  const progress = Math.round(Math.max(0, Math.min(g.status === "completed" && g.assetIds?.length ? 100 : 99, raw)));
  const color = g.status === "failed" ? "text-danger" : g.status === "cancelled" ? "text-muted" : g.status === "completed" ? "text-success" : g.status === "blocked" ? "text-amber" : "text-lavender";
  return <div role="progressbar" aria-label={`${g.title} generation progress`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress} aria-valuetext={`${progress}% — ${g.stage}`} className={`relative shrink-0 ${small ? "size-12" : "size-20"} ${color}`}>
    <svg viewBox="0 0 80 80" className="size-full -rotate-90" aria-hidden="true">
      <circle cx="40" cy="40" r="34" fill="none" stroke="currentColor" strokeWidth="5" opacity="0.15" />
      <circle cx="40" cy="40" r="34" fill="none" stroke="currentColor" strokeWidth="5" strokeLinecap="round" pathLength="100" strokeDasharray={`${progress} 100`} />
    </svg>
    <span className={`absolute inset-0 grid place-items-center font-semibold tnum ${small ? "text-[11px]" : "text-[17px]"}`}>{progress}%</span>
  </div>;
}

const tones: Record<Generation["status"], Tone> = { queued: "muted", running: "primary", blocked: "amber", failed: "danger", cancelled: "muted", completed: "success" };
export function generationLabel(g: Generation) {
  if (g.status === "running") return /plan/i.test(g.stage) ? "Planning" : /quality|qa|verif/i.test(g.stage) ? "Quality check" : "Generating";
  return { queued: "Queued", blocked: /approv|script/i.test(g.stage) ? "Needs approval" : "Blocked", failed: "Failed", cancelled: "Cancelled", completed: "Outputs ready" }[g.status];
}

function sourceHref(brandId: string, g: Generation) {
  if (g.sourceTool === "ideas.accept" && g.sourceId) return brandPath(brandId, `inspiration?tab=ideas&idea=${encodeURIComponent(g.sourceId)}#idea-${encodeURIComponent(g.sourceId)}`);
  if (g.sourceTool === "ideas.from_reference" && g.sourceId) return brandPath(brandId, `inspiration?ref=${encodeURIComponent(g.sourceId)}`);
  if (g.sourceTool.startsWith("recommendations.") && g.sourceId) return brandPath(brandId, `home#recommendation-${encodeURIComponent(g.sourceId)}`);
  return null;
}

export function GenerationCard({ brandId, generation: g, compact = false }: { brandId: string; generation: Generation; compact?: boolean }) {
  const source = sourceHref(brandId, g);
  return <article id={`generation-${g.runId}`} aria-label={`Generation: ${g.title}`} className="overflow-hidden rounded-card border border-line bg-surface">
    <div className={`flex items-center justify-center gap-4 bg-surface-2 ${compact ? "p-4" : "min-h-[160px] flex-col p-5"}`}>
      <GenerationRing generation={g} small={compact} />
      <Pill tone={tones[g.status] ?? "muted"}>{generationLabel(g)}</Pill>
    </div>
    <div className="p-4">
      <h3 className="text-[14px] font-semibold">{g.title}</h3>
      <p className="mt-1 text-[12.5px] text-muted" role="status">{g.stage}</p>
      {g.error && <p className="mt-2 break-words text-[12px] text-danger" role="alert">{g.error}</p>}
      {g.status === "completed" && <p className="mt-1 text-[12px] text-muted">Generation complete. Approval and publishing are separate.</p>}
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-[12px] text-link">
        <Link className="hover:underline" to={generationHref(brandId, g)}>View flow</Link>
        <Link className="hover:underline" to={brandPath(brandId, `creations?flow=${encodeURIComponent(g.flowId)}&run=${encodeURIComponent(g.runId)}`)}>View creations</Link>
        {source && <Link className="hover:underline" to={source}>View source</Link>}
      </div>
    </div>
  </article>;
}

/** Source entrypoints can find their receipt after refresh without executing the action again. */
export function SourceGeneration({ brandId, sourceId, sourceTool }: { brandId: string; sourceId: string; sourceTool?: string }) {
  const q = useTool<{ flows?: { generation?: Generation | null }[] }>("flows.list", { brandId, limit: 100 }, { staleTime: 0, refetchInterval: 3000 });
  const g = q.data?.flows?.map(f => f.generation).find(g => g?.sourceId === sourceId && (!sourceTool || g.sourceTool === sourceTool));
  return g ? <div className="mt-3"><GenerationCard brandId={brandId} generation={g} compact /></div> : null;
}

export function GenerationFlowDetail({ brandId, generation: g, detail }: { brandId: string; generation: Generation; detail?: FlowDetail }) {
  return <main className="mx-auto min-h-screen w-full max-w-5xl p-6 sm:p-10">
    <ButtonLink to={brandPath(brandId, "flows")} variant="ghost" size="sm">← All flows</ButtonLink>
    <h1 className="mb-2 mt-5 text-2xl font-semibold">{g.title}</h1>
    <p className="mb-6 text-sm text-muted">This flow follows the original generation pipeline. Refreshing only checks progress; it never resubmits work.</p>
    <GenerationCard brandId={brandId} generation={g} />
    {detail?.graph?.nodes?.length ? <section className="mt-6 rounded-card border border-line bg-surface p-5" aria-label="Actual pipeline stages">
      <h2 className="font-semibold">Pipeline stages</h2>
      <ol className="mt-3 flex flex-col gap-3">{detail.graph.nodes.map(node => {
        const state = detail.lastRun?.nodeStates?.[node.id];
        return <li key={node.id} className="flex flex-wrap items-center justify-between gap-2 text-sm"><span>{node.title ?? node.id}</span><Pill tone={state?.status === "failed" ? "danger" : state?.status === "done" ? "success" : "muted"}>{state?.status ?? "Not reported"}</Pill>{(state?.error || state?.reason) && <p className="w-full text-xs text-muted">{state.error ?? state.reason}</p>}</li>;
      })}</ol>
    </section> : null}
    {Object.values(detail?.outputs ?? {}).some(group => group.active.length) && <section className="mt-6 rounded-card border border-line bg-surface p-5" aria-label="Retained artifacts">
      <h2 className="font-semibold">Retained artifacts</h2><p className="mt-1 text-xs text-muted">Availability is not QA approval. Review each creation for its verdict and next instructions.</p>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">{Object.entries(detail?.outputs ?? {}).flatMap(([nodeId, group]) => group.active.map(output => <figure key={output.id} className="overflow-hidden rounded-xl border border-line">
        <CreativeMedia url={output.url} mediaType={output.kind} controls={output.kind === "video"} fit="contain" className="h-64 w-full" />
        <figcaption className="p-3 text-xs"><div>{detail?.graph.nodes.find(n => n.id === nodeId)?.title ?? nodeId} · V{output.version}</div>{safeMediaHref(output.url) && <a href={safeMediaHref(output.url)} download target="_blank" rel="noreferrer" className="text-link">Download artifact</a>}</figcaption>
      </figure>))}</div>
    </section>}
    <section className="mt-6 rounded-card border border-line bg-surface p-5" aria-label="Generated outputs">
      <h2 className="font-semibold">Creations</h2>
      {g.creativeIds.length ? <ul className="mt-3 flex flex-col gap-2">{g.creativeIds.map((id, i) => <li key={id}><Link className="text-sm text-link hover:underline" to={brandPath(brandId, `creations?creative=${encodeURIComponent(id)}&flow=${encodeURIComponent(g.flowId)}&run=${encodeURIComponent(g.runId)}`)}>Open creation {i + 1}{g.status === "blocked" ? " — review approval" : ""}</Link></li>)}</ul> : <p className="mt-2 text-sm text-muted">No outputs yet. Your request is saved and will appear here as stages finish.</p>}
      <p className="mt-4 text-xs text-muted">Nothing publishes or goes live automatically. Required script, creative and publishing approvals remain in place.</p>
    </section>
  </main>;
}

export function GenerationReceiptPanel({ brandId, flowId }: { brandId: string; flowId: string }) {
  const q = useTool<{ generation?: Generation | null }>("flows.get", { brandId, flowId }, { staleTime: 0, refetchInterval: d => d?.generation && !generationActive(d.generation) ? false : 3000 });
  if (q.error) return <ErrorState compact error={q.error} onRetry={() => void q.refetch()} />;
  if (q.isLoading) return <Skeleton className="mb-5 h-36 rounded-card" />;
  return q.data?.generation ? <GenerationCard brandId={brandId} generation={q.data.generation} compact /> : null;
}
